import { Directive, inject, Input, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Automation, ChoicesInterface, RuleInterface, WorkflowStepInterface } from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReplaySubject } from 'rxjs';
import { AutomationVariableMenuService } from '../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { initialTemplateCreationQueryParam } from '../../../common/constants';
import { TriggerService } from '../../trigger.service';

@Directive()
export abstract class CommonTriggerPanelComponent implements OnInit {
  @Input() triggerChoices: ChoicesInterface;
  @Input() taskDefinitionId: string;
  @Input() automation: Automation;
  @Input() step: WorkflowStepInterface;
  @Input() isWaitingForEvent: boolean;
  @Input() waitForEventData: string;

  /** Emits true when saving is enabled, false when it is not.*/
  savingEnabled$ = new ReplaySubject<boolean>();

  // getControlsToValidateOnSave should return all controls to be touched and updated for validation display purposes
  abstract getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[];
  abstract isFormValid(): boolean;
  abstract getBaseRules(): RuleInterface[];
  abstract fillRuleValues(rules: RuleInterface[]): RuleInterface[];

  /**
   * getData returns the data that is configured in the TriggerPanel (This is not data used to generate the rules)
   * Most triggers won't need to implement getData so it's not something that is required to be implemented, but it can
   * be overridden when needed.
   */
  getData(): any {
    return {};
  }

  /**
   * Determines if we are in a template context.
   * Returns true if the automation is a template or in initial template creation mode.
   */
  get isTemplateContext(): boolean {
    const isInitialTemplateCreation = !!this.route.snapshot.queryParams[initialTemplateCreationQueryParam];
    return this.automation.isTemplate || isInitialTemplateCreation;
  }

  protected readonly route: ActivatedRoute = inject(ActivatedRoute);
  protected readonly snackbarService: SnackbarService = inject(SnackbarService);
  protected readonly translateService: TranslateService = inject(TranslateService);
  protected readonly triggerService: TriggerService = inject(TriggerService);
  protected readonly automationVariableMenuService: AutomationVariableMenuService =
    inject(AutomationVariableMenuService);

  constructor() {
    this.savingEnabled$.next(false);
  }

  ngOnInit(): void {
    this.automationVariableMenuService.init(this.automation, []);
  }
}
